import classNames from 'classnames/bind';
import Tree, { TreeNode } from 'rc-tree';
import { TreeNodeProps } from 'rc-tree/es/TreeNode';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Input } from 'semantic-ui-react';
import Button, { ButtonMode, ButtonSize } from '../Button';
import Checkbox from '../Checkbox';
import Divider from '../Divider';
import Icon, { ICONS } from '../Icon';
import { filterData, getChildNodesToToggle, getParentKeys, mapToParentMap } from './TreeSelect.logic';
import { TreeNodeModel } from './TreeSelect.types';
import colors from '../../scss/_colors-export.module.scss';

import styles from './TreeSelect.module.scss';

const cx = classNames.bind(styles);

export const getSwitcherIconTestId = (key: string): string => `${key}-switcherIcon`;
export const getCheckboxTestId = (key: string): string => `${key}-checkbox`;

const switcherIcon = (props: TreeNodeProps) => {
  const { isLeaf, expanded, data } = props;
  if (isLeaf) {
    return null;
  }

  return (
    <div className={cx('switcher-icon', { expanded })}>
      <Icon height={8} width={8} name={ICONS.CaretDown} dataTestId={getSwitcherIconTestId(data?.key as string)} />
    </div>
  );
};

const renderTreeNodes = (
  data: TreeNodeModel[],
  isSelected: (string) => boolean,
  isAllChildrenSelected: (string) => boolean,
  isIndeterminate: (string) => boolean,
  onCheck: (string, boolean) => void,
  disabled?: boolean,
  treeLevel: number = 0
) =>
  data.map((item) => {
    const allSelectedChildren = isAllChildrenSelected(item.key);
    const checked = isSelected(item.key) || allSelectedChildren || false;
    const indeterminate = !checked && isIndeterminate(item.key);

    if (item.children) {
      return (
        <TreeNode
          data-testid="filter-tree-node"
          className={cx(`tree-level-${treeLevel}`)}
          key={item.key}
          title={
            <Checkbox
              className={cx('checkbox')}
              dataTestId={getCheckboxTestId(item.key)}
              label={item.title}
              checked={checked}
              indeterminate={indeterminate}
              onChange={(a, data) => onCheck(item.key, data.checked)}
              disabled={disabled}
            />
          }
        >
          {renderTreeNodes(
            item.children,
            isSelected,
            isAllChildrenSelected,
            isIndeterminate,
            onCheck,
            disabled,
            treeLevel + 1
          )}
        </TreeNode>
      );
    }
    return (
      <TreeNode
        data-testid="filter-tree-node"
        className={cx(`tree-level-${treeLevel}`)}
        key={item.key}
        title={
          <Checkbox
            className={cx('checkbox')}
            dataTestId={getCheckboxTestId(item.key)}
            label={item.title}
            checked={checked}
            indeterminate={false}
            onChange={(a, data) => onCheck(item.key, data.checked)}
            disabled={disabled}
          />
        }
      />
    );
  });

interface TreeSelectProps {
  data: TreeNodeModel[];
  checkedKeys: string[];
  setCheckedKeys: (checkedKeys: string[]) => void;
  treeHeight?: number;
  showReset?: boolean;
  showSearch?: boolean;
  inputAutoFocus?: boolean;
  plainTheme?: boolean;
  disabled?: boolean;
}

export interface TreeSelectHandle {
  reset: () => void;
}

const TreeSelect = (
  {
    data,
    checkedKeys,
    setCheckedKeys,
    treeHeight = 400,
    showReset = true,
    showSearch = true,
    plainTheme = false,
    inputAutoFocus = true,
    disabled,
  }: TreeSelectProps,
  ref
): React.ReactElement => {
  const [filteredData, setFilteredData] = useState(data);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const selectedSet = useMemo(() => new Set(checkedKeys), [checkedKeys]);

  const parentMap = useMemo(() => mapToParentMap(data), [data]);

  const isSelected = (key: string) => selectedSet.has(key);
  const isIndeterminate = (key: string) =>
    parentMap[key]?.some((childNodeId: string) => selectedSet.has(childNodeId)) || false;

  useImperativeHandle(
    ref,
    () => ({
      reset: onReset,
    }),
    []
  );

  useEffect(() => {
    setFilteredData(inputValue ? filterData(data, inputValue) : data);
  }, [inputValue]);

  useEffect(() => {
    setExpandedKeys(
      inputValue
        ? getParentKeys(filteredData)
        : Object.keys(parentMap).filter((key) => !isSelected(key) && isIndeterminate(key))
    );
  }, [inputValue, filteredData]);

  const onCheck = (key: string, checked: boolean) => {
    const { childNodesToToggle, path } = getChildNodesToToggle(data, key);

    let array = checked
      ? [...checkedKeys, ...childNodesToToggle]
      : checkedKeys.filter((value) => !childNodesToToggle.includes(value)).filter((value) => !path.includes(value));
    array = array.filter((v, i) => array.indexOf(v) === i);

    if (checked) {
      // Auto-check parents if all children are selected
      path.forEach((p) => {
        const allChildrenSelected = parentMap[p]?.every((childNodeId: string) => array.includes(childNodeId));
        if (allChildrenSelected) {
          array.push(p);
        }
      });
    }

    setCheckedKeys(array);
  };

  const onExpand = (expandedKeys) => {
    setExpandedKeys(expandedKeys);
  };

  const onInputChange = (value) => {
    setInputValue(value);
  };

  const isAllChildrenSelected = (key: string) =>
    parentMap[key]?.every((childNodeId: string) => selectedSet.has(childNodeId));

  const onReset = () => {
    setCheckedKeys([]);
    setExpandedKeys([]);
    setInputValue('');
  };

  const renderNoData = () => {
    if (filteredData.length) {
      return null;
    }
    return (
      <div className={cx('no-results')} data-testid="filter-tree-no-results">
        No filters matching
        <br />
        &quot;{inputValue}&quot;
      </div>
    );
  };

  const renderReset = () => (
    <Button data-testid="filter-tree-reset" mode={ButtonMode.TERTIARY} size={ButtonSize.SMALL} onClick={onReset}>
      <Icon name={ICONS.Clear} width={12} height={12} />
      Reset
    </Button>
  );

  return (
    <div className={cx('body', { 'plain-theme': plainTheme })}>
      {showSearch && (
        <Input
          className={cx('search')}
          fluid
          icon
          iconPosition="left"
          onChange={(e, d) => onInputChange(d.value)}
          placeholder="Search"
          value={inputValue}
          autoFocus={inputAutoFocus}
          disabled={disabled}
        >
          <span className={cx('search-icon')}>
            <Icon name={ICONS.Search} height={12} width={12} color={colors.tertiary600} />
          </span>
          <input className={cx('search-input')} />
        </Input>
      )}
      <Tree
        className={cx('tree', { 'plain-theme': plainTheme })}
        checkable={false}
        checkedKeys={checkedKeys}
        expandedKeys={expandedKeys}
        onExpand={onExpand}
        selectable={false}
        showIcon={false}
        switcherIcon={switcherIcon}
        height={treeHeight}
        itemHeight={32}
      >
        {renderTreeNodes(filteredData, isSelected, isAllChildrenSelected, isIndeterminate, onCheck, disabled)}
      </Tree>
      {renderNoData()}
      {showReset && (
        <>
          <div className={cx('divider')}>
            <Divider />
          </div>
          {renderReset()}
        </>
      )}
    </div>
  );
};

export default forwardRef<TreeSelectHandle, TreeSelectProps>(TreeSelect);
